import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Alert,
  Button,
  Icon,
  Input,
  PageHeading,
  Pills,
  Stepper,
  Text,
} from 'barte-design-system';
import {
  ArrowLeft,
  ArrowRight,
  ArrowRight as ArrowDiff,
  CheckCircleIcon,
  FileXls,
  Link as LinkIcon,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import {
  COMPANY_MOCK,
  INITIAL_PLANOS,
  type PlanoTaxa,
} from '../CadastroPlanoTaxas/sharedData';
import { TaxasCartaoGrid, type TaxasCartaoState } from '../TaxasCompanyCopiaCola/TaxasCartaoGrid';
import './EdicaoMassaPlano.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3;

const STEPS = [
  { label: 'Upload' },
  { label: 'Revisão' },
  { label: 'Processamento' },
];

// ─── Mock current vs new ──────────────────────────────────────────────────────

const TAXAS_BASE_CURRENT: { label: string; current: string }[] = [
  { label: 'PIX',                 current: 'R$ 0,70' },
  { label: 'Boleto',              current: 'R$ 1,50' },
  { label: 'Débito',              current: '1,05%' },
  { label: 'Antifraude',          current: 'R$ 0,29' },
  { label: 'Processamento',       current: 'R$ 0,29' },
  { label: 'Antecipação (%CDI)',  current: '130%' },
];

const TAXAS_BASE_NEW: Record<string, string> = {
  'PIX': 'R$ 0,65',
  'Boleto': 'R$ 1,40',
  'Débito': '0,95%',
  'Antifraude': 'R$ 0,25',
  'Processamento': 'R$ 0,29',
  'Antecipação (%CDI)': '125%',
};

interface DiffGroup {
  id: string;
  label: string;
  mccs: string[];
  tipos: string[];
  current: number[][]; // 4 × 5 — taxa atual (MDR consolidado)
  next: number[][];    // 4 × 5 — novo MDR
  antecipacao: number; // delta de antecipação aplicado ao novo MDR
  antecipacaoLabel: string; // ex.: "130% da CDI"
}

const DIFF_GROUPS: DiffGroup[] = [
  {
    id: 'grp-1',
    label: 'MCCs - Grupo 1',
    mccs: ['8999'],
    tipos: ['online', 'pos', 'tap'],
    antecipacao: 1.99,
    antecipacaoLabel: '130% da CDI',
    current: [
      [1.05, 1.37, 3.04, 0.83, 2.32],
      [0.34, 2.23, 3.51, 1.11, 3.57],
      [0.46, 2.96, 3.73, 1.11, 3.97],
      [3.45, 3.45, 3.45, 3.45, 3.45],
    ],
    next: [
      [0.95, 1.30, 2.95, 0.83, 2.20],
      [0.30, 2.15, 3.40, 1.05, 3.45],
      [0.42, 2.85, 3.60, 1.05, 3.80],
      [3.30, 3.30, 3.30, 3.30, 3.30],
    ],
  },
  {
    id: 'grp-2',
    label: 'MCCs - Grupo 2',
    mccs: ['7372', '5732'],
    tipos: ['online', 'pos'],
    antecipacao: 1.60,
    antecipacaoLabel: '125% da CDI',
    current: [
      [0.95, 1.25, 2.85, 0.75, 2.15],
      [0.28, 2.05, 3.30, 1.00, 3.40],
      [0.40, 2.75, 3.55, 1.05, 3.80],
      [3.20, 3.20, 3.20, 3.20, 3.20],
    ],
    next: [
      [0.90, 1.20, 2.75, 0.70, 2.10],
      [0.25, 1.95, 3.20, 0.95, 3.30],
      [0.38, 2.65, 3.45, 1.00, 3.70],
      [3.10, 3.10, 3.10, 3.10, 3.10],
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function EdicaoMassaPlano() {
  const navigate = useNavigate();
  const { planoId } = useParams<{ planoId: string }>();

  const plano: PlanoTaxa | undefined = useMemo(() => {
    const id = parseInt(planoId ?? '', 10);
    return INITIAL_PLANOS.find((p) => p.id === id);
  }, [planoId]);
  const planoNome = plano?.nome ?? 'Plano';

  const [step, setStep] = useState<WizardStep>(1);

  // Step 1
  const [sheetFile, setSheetFile] = useState<File | null>(null);
  const [sheetLink, setSheetLink] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [step1Error, setStep1Error] = useState<string | null>(null);

  // Step 3
  const [processingPhase, setProcessingPhase] = useState<0 | 1 | 2>(0);
  const [showSuccess, setShowSuccess] = useState(false);

  // Loading entre step 1 → step 2 ("Importando dados da planilha")
  const [importLoading, setImportLoading] = useState(false);

  // ─── Step 1: upload / link ───────────────────────────────────────────────

  function validateAndSetFile(file: File | null) {
    setStep1Error(null);
    if (!file) { setSheetFile(null); return; }
    const ok = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!ok) {
      setStep1Error('Formato inválido. Aceitamos XLSX, XLS ou CSV.');
      setSheetFile(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setStep1Error('O arquivo excede o limite de 10 MB.');
      setSheetFile(null);
      return;
    }
    setSheetFile(file);
    setSheetLink('');
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  const linkValido = useMemo(() => {
    const trimmed = sheetLink.trim();
    if (!trimmed) return false;
    return /^https?:\/\/.+/.test(trimmed);
  }, [sheetLink]);

  const step1Ready = sheetFile !== null || linkValido;

  // ─── Nav ─────────────────────────────────────────────────────────────────

  function handleNext() {
    if (step === 1) {
      if (!step1Ready) {
        setStep1Error('Faça upload de um arquivo ou cole um link válido.');
        return;
      }
      setStep1Error(null);
      setImportLoading(true);
      setTimeout(() => {
        setImportLoading(false);
        setStep(2);
      }, 2000);
    } else if (step === 2) {
      setStep(3);
    }
  }

  function handlePrev() {
    if (step === 2) setStep(1);
    else if (step === 1) navigate(`/backoffice/taxas-company/plano/${planoId}`);
  }

  // ─── Processamento ───────────────────────────────────────────────────────

  useEffect(() => {
    if (step !== 3) return;
    setProcessingPhase(0);
    setShowSuccess(false);
    const t1 = setTimeout(() => setProcessingPhase(1), 3000);
    const t2 = setTimeout(() => setProcessingPhase(2), 6000);
    const t3 = setTimeout(() => setShowSuccess(true), 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [step]);

  // ─── Helpers de diff ─────────────────────────────────────────────────────

  function DiffCell({ atual, novo }: { atual: string; novo: string }) {
    const changed = atual.trim() !== novo.trim();
    if (!changed) {
      return <span className="emp-diff-cell emp-diff-cell--same">{atual}</span>;
    }
    return (
      <span className="emp-diff-cell emp-diff-cell--changed">
        <span className="emp-diff-old">{atual}</span>
        <Icon icon={ArrowDiff} size={12} weight="bold" />
        <span className="emp-diff-new">{novo}</span>
      </span>
    );
  }

  // ─── Renders ─────────────────────────────────────────────────────────────

  function renderStep1() {
    return (
      <div className="emp-step-body">
        <div className="emp-field">
          <Text variant="label-md" className="emp-field-label">Origem dos novos dados</Text>

          {!sheetFile && (
            <label
              htmlFor="emp-file-input"
              className={`emp-dropzone${isDragging ? ' is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
            >
              <div className="emp-dropzone-icon">
                <Icon icon={UploadSimple} size={32} weight="regular" />
              </div>
              <Text variant="body-base">
                <span className="emp-dropzone-link">Clique para fazer upload</span> ou arraste e solte
              </Text>
              <Text variant="caption-md">Arquivos suportados: XLSX, XLS, CSV (até 10 MB)</Text>
              <input
                id="emp-file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                className="emp-dropzone-input"
                onChange={onFilePick}
              />
            </label>
          )}

          {sheetFile && (
            <div className="emp-file-card">
              <Icon icon={FileXls} size={28} weight="regular" />
              <div className="emp-file-card-meta">
                <Text variant="label-base" className="emp-file-card-name">{sheetFile.name}</Text>
                <Text variant="caption-md" className="emp-file-card-size">
                  {(sheetFile.size / 1024).toFixed(1)} KB
                </Text>
              </div>
              <button
                className="emp-file-card-remove"
                onClick={() => setSheetFile(null)}
                aria-label="Remover arquivo"
                type="button"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}

          <div className="emp-or-divider">
            <span className="emp-or-line" />
            <Text variant="caption-md">ou</Text>
            <span className="emp-or-line" />
          </div>

          <Input
            label="Link do Google Sheets ou Drive"
            placeholder="https://docs.google.com/spreadsheets/..."
            icon={LinkIcon as any}
            value={sheetLink}
            onChange={(e) => {
              setSheetLink((e.target as HTMLInputElement).value);
              if (sheetFile) setSheetFile(null);
            }}
            disabled={sheetFile !== null}
          />
        </div>

        {step1Error && (
          <Alert
            variant="error"
            title="Não foi possível avançar"
            description={step1Error}
            showCloseButton={false}
          />
        )}
      </div>
    );
  }

  function renderStep2() {
    const totalChanged = TAXAS_BASE_CURRENT.filter(
      (r) => r.current.trim() !== (TAXAS_BASE_NEW[r.label] ?? '').trim(),
    ).length + DIFF_GROUPS.reduce((sum, g) => {
      let n = 0;
      for (let r = 0; r < g.current.length; r++) {
        for (let c = 0; c < g.current[r].length; c++) {
          if (g.current[r][c] !== g.next[r][c]) n++;
        }
      }
      return sum + n;
    }, 0);

    // Diff string helper: "atual" se igual, ou JSX com atual → novo.
    function diffStr(atual: number, novo: number): string {
      const f = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
      if (Math.abs(atual - novo) < 0.001) return f(atual);
      return `${f(atual)} → ${f(novo)}`;
    }
    // Constrói TaxasCartaoState com strings de diff por célula
    function buildDiffState(g: typeof DIFF_GROUPS[number]): TaxasCartaoState {
      return {
        '1x':    g.current[0].map((v, i) => diffStr(v, g.next[0][i])),
        '2-6':   g.current[1].map((v, i) => diffStr(v, g.next[1][i])),
        '7-12':  g.current[2].map((v, i) => diffStr(v, g.next[2][i])),
        '13-24': g.current[3].map((v, i) => diffStr(v, g.next[3][i])),
        // Débito: hardcoded mock (não temos no DIFF_GROUPS original)
        debito:  ['1,05%', '1,15%', '1,25%', '0,85%', '1,10%'],
      };
    }

    return (
      <div className="emp-step-body">
        <Alert
          variant="info"
          title={`${totalChanged} taxa${totalChanged !== 1 ? 's' : ''} serão alterada${totalChanged !== 1 ? 's' : ''}`}
          description="Confira o diff abaixo antes de confirmar. Cells alteradas mostram o atual à esquerda e o novo à direita."
          showCloseButton={false}
        />

        {/* ── Taxas horizontal com diff ──────────────────────────────── */}
        <div className="emp-taxas-base-grid">
          {(['PIX', 'Boleto', 'Antifraude', 'Processamento'] as const).map((label) => (
            <div key={`l-${label}`} className="emp-tb-label">{label}</div>
          ))}
          {(['PIX', 'Boleto', 'Antifraude', 'Processamento'] as const).map((label) => {
            const atual = TAXAS_BASE_CURRENT.find((r) => r.label === label)?.current ?? '—';
            const novo = TAXAS_BASE_NEW[label] ?? atual;
            return (
              <div key={`v-${label}`} className="emp-tb-value">
                <DiffCell atual={atual} novo={novo} />
              </div>
            );
          })}
        </div>

        <div className="emp-divider" />

        {/* ── Grupos ─────────────────────────────────────────────────── */}
        <div className="emp-field">
          {DIFF_GROUPS.map((g, idx) => (
            <div key={g.id} className="emp-group-block">
              <div className="emp-group-pill-row">
                <Pills label={`Grupo ${idx + 1}`} state="default" variant="fill" size="sm" />
                {g.tipos.map((t) => {
                  const label = t === 'online' ? 'Online' : t === 'pos' ? 'POS' : 'Tap';
                  return (
                    <Pills key={t} label={label} state="warning" variant="light" size="sm" />
                  );
                })}
                {g.mccs.map((m) => (
                  <Pills key={m} label={m} state="accent" variant="light" size="sm" />
                ))}
                <div className="emp-group-pill-spacer" />
                <span className="emp-antecipacao-pill">
                  Antecipação: <strong>{g.antecipacaoLabel}</strong>
                </span>
              </div>

              <div className="emp-readonly-wrap">
                <TaxasCartaoGrid
                  readOnly
                  value={buildDiffState(g)}
                  onChange={() => {}}
                  expanded={{}}
                  onToggleExpand={() => {}}
                  extraRows={[{ key: 'debito', label: 'Débito' }]}
                  renderCell={(_rowKey, _c, raw) => {
                    if (!raw) return <span className="emp-diff-cell--cartao emp-diff-cell--same">—</span>;
                    if (!raw.includes(' → ')) {
                      return <span className="emp-diff-cell--cartao emp-diff-cell--same">{raw}</span>;
                    }
                    const [atual, novo] = raw.split(' → ');
                    return (
                      <span className="emp-diff-cell emp-diff-cell--cartao emp-diff-cell--changed">
                        <span className="emp-diff-old">{atual}</span>
                        <Icon icon={ArrowDiff} size={11} weight="bold" />
                        <span className="emp-diff-new">{novo}</span>
                      </span>
                    );
                  }}
                />
              </div>

              {idx < DIFF_GROUPS.length - 1 && <div className="emp-group-divider" />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderStep3() {
    const phases = ['Calculando variações de taxa…', 'Aplicando alterações…', 'Tudo pronto!'];
    if (showSuccess) {
      return (
        <div className="emp-state-screen">
          <Icon icon={CheckCircleIcon} size={56} weight="fill" className="emp-success-icon" />
          <Text variant="body-xl" className="emp-state-screen-title">
            Plano "{planoNome}" atualizado com sucesso
          </Text>
          <Pills label="Edição em massa aplicada" state="success" variant="light" size="base" />
        </div>
      );
    }
    return (
      <div className="emp-state-screen">
        <div className="emp-spinner" aria-label="Processando" />
        <Text variant="body-xl" className="emp-state-screen-title" role="status" aria-live="polite">
          {phases[processingPhase]}
        </Text>
      </div>
    );
  }

  function renderFooter() {
    if (step === 3) {
      if (showSuccess) {
        return (
          <>
            <div />
            <div className="emp-footer-right">
              <Button onClick={() => navigate(`/backoffice/taxas-company/plano/${planoId}`)}>
                Voltar para o plano
              </Button>
            </div>
          </>
        );
      }
      return null;
    }
    return (
      <>
        <Button
          variant="ghost"
          onClick={handlePrev}
          iconLeft={<Icon icon={ArrowLeft} size={18} weight="regular" />}
        >
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </Button>
        <Button
          onClick={handleNext}
          iconRight={<Icon icon={ArrowRight} size={18} weight="regular" />}
          disabled={step === 1 && !step1Ready}
        >
          {step === 2 ? 'Aplicar alterações' : 'Próximo'}
        </Button>
      </>
    );
  }

  return (
    <BackofficeLayout activeItem="taxas-company-copia-cola">
      <main className="content">
        <div className="content-inner emp-page">
          <PageHeading title={`Editando plano ${planoNome}...`} />

          <div className="emp-company-pill">
            <Pills
              label={`Empresa: ${COMPANY_MOCK.nome}`}
              state="default"
              variant="light"
              size="sm"
            />
          </div>

          <div className="emp-stepper">
            <Stepper steps={STEPS} currentStep={step} />
          </div>

          {importLoading ? (
            <div className="emp-state-screen">
              <div className="emp-spinner" aria-label="Importando" />
              <Text variant="body-xl" className="emp-state-screen-title">
                Importando dados da planilha…
              </Text>
            </div>
          ) : (
            <>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </>
          )}

          {!importLoading && <div className="emp-footer">{renderFooter()}</div>}
        </div>
      </main>
    </BackofficeLayout>
  );
}
